import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';

import CustomerCard from './customer-card';
import { TCustomer } from '../schemas';

type CustomerListProps = {
  customers: TCustomer[];
  totalCount: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  onExport?: () => void;
  isExporting?: boolean;
};

const CustomerList = ({
  customers,
  totalCount,
  page,
  limit,
  onPageChange,
  isLoading = false,
  onExport,
  isExporting = false,
}: CustomerListProps) => {
  const totalPages = Math.ceil(totalCount / limit);

  // Show shimmer loading state
  if (isLoading) {
    return (
      <Box>
        <Grid container spacing={2} alignItems="flex-start">
          {Array.from({ length: limit }).map((_, index) => (
            <Grid item xs={12} key={index}>
              <Box sx={styles.shimmerCard}>
                <Box sx={styles.shimmerColumn}>
                  <Skeleton variant="text" width="50%" height={16} />
                  <Skeleton variant="text" width="90%" height={20} />
                </Box>
                <Box sx={styles.shimmerColumn}>
                  <Skeleton variant="text" width="60%" height={16} />
                  <Skeleton variant="text" width="80%" height={20} />
                </Box>
                <Box sx={styles.shimmerColumnLarge}>
                  <Skeleton variant="text" width="40%" height={16} />
                  <Skeleton variant="text" width="90%" height={20} />
                </Box>
                <Box sx={styles.shimmerColumn}>
                  <Skeleton variant="text" width="50%" height={16} />
                  <Skeleton variant="text" width="70%" height={20} />
                </Box>
                <Box sx={styles.shimmerColumnLarge}>
                  <Skeleton variant="text" width="45%" height={16} />
                  <Skeleton variant="text" width="85%" height={20} />
                </Box>
                <Box sx={styles.shimmerColumn}>
                  <Skeleton variant="text" width="55%" height={16} />
                  <Skeleton variant="text" width="65%" height={20} />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!customers || customers.length === 0) {
    return (
      <Box sx={styles.emptyContainer}>
        <Typography sx={styles.emptyText}>No customers found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Export Button */}
      {onExport && (
        <Box sx={styles.headerContainer}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={onExport}
            disabled={isExporting || isLoading}
            sx={styles.exportButton}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Box>
      )}

      <Grid container spacing={2} alignItems="flex-start">
        {customers.map((customer) => (
          <Grid item xs={12} key={customer._id}>
            <CustomerCard customer={customer} />
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <Box sx={styles.paginationContainer}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange(newPage)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
          <Typography sx={styles.paginationText}>
            Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, totalCount)} of {totalCount} customers
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CustomerList;

const styles = {
  headerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 2,
  },
  exportButton: {
    backgroundColor: '#1a1a1a',
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: '#333',
    },
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    backgroundColor: '#f5f5f5',
    borderRadius: 2,
    minHeight: '150px',
  },
  emptyText: {
    color: '#666',
    fontSize: 14,
    fontFamily: 'GilroyRegular',
  },
  paginationContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1,
    marginTop: 3,
    paddingTop: 2,
    borderTop: '1px solid #eee',
  },
  paginationText: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
    color: '#666',
  },
  shimmerCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    backgroundColor: '#FFF',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    padding: 0,
    overflow: 'hidden',
    minHeight: '70px',
  },
  shimmerColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    flex: 0.8,
    minWidth: 0,
    padding: '14px 12px',
    borderRight: '1px solid #f0f0f0',
    justifyContent: 'center',
  },
  shimmerColumnLarge: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    flex: 1.3,
    minWidth: 0,
    padding: '14px 12px',
    borderRight: '1px solid #f0f0f0',
    justifyContent: 'center',
  },
};
